import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useProjectId } from '~/hooks/context/useProjectId';
import { api } from '~/trpc-extra/api';

export function useTestReRun({ showWarning }: { showWarning?: boolean }) {
  const trpcUtils = api.useUtils();
  const projectId = useProjectId();

  const { mutateAsync: reRun, isPending: isReRunning } = api.testRun.reRun.useMutation({
    onMutate: async (input) => {
      notifications.show({
        title: `Rerunning ${input.runIds.length === 1 ? 'test' : 'tests'}...`,
        message: 'This may take a while depending on the number of steps.',
        autoClose: 10000,
      });

      // Wait for 10 seconds before invalidating. The DB might be slow to update.
      await new Promise((resolve) => setTimeout(resolve, 10000));
      await Promise.allSettled([
        trpcUtils.suiteRun.list.invalidate({ projectId: projectId || '' }),
        ...input.runIds.flatMap((runId) => [
          trpcUtils.suiteRun.get.invalidate({ runId }),
          trpcUtils.testRun.get.invalidate({ runId }),
        ]),
      ]);
    },
    onError: (e) => {
      // TODO: if this is a timeout error, we should should swallow it
      console.error(e);
      notifications.show({ message: 'Error rerunning test', color: 'red' });
    },
  });

  const handleReRun = async (runIds: string[]) => {
    if (showWarning) {
      modals.openConfirmModal({
        title: 'Warning: Test Interference',
        zIndex: 1000,
        children: (
          <span>
            Another test in the same suite might be running and rerunning the current test could interfere with it
          </span>
        ),
        onConfirm: async () => {
          await reRun({ runIds });
        },
        labels: { confirm: 'Rerun', cancel: 'Cancel' },
      });
    } else {
      await reRun({ runIds });
    }
  };

  return {
    handleReRun,
    isReRunning,
  };
}
